import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import { Global, css } from '@emotion/react';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  useTheme,
  useMediaQuery,
  styled,
  CircularProgress,
  alpha,
  InputBase,
  ClickAwayListener
} from '@mui/material';
import { keyframes } from '@mui/system';
import {
  Minimize2,
  CheckCircle,
  Frown,
  ArrowUp,
  PlusCircle,
  ClipboardList,
  Trash2
} from 'lucide-react';

// Importa el servicio de Cash Flow desde la ubicación correcta
import agtCashFlowService from '../../services/AGTUps-product-services/agtCashFlowService';

// Inyección de estilos globales para animaciones personalizadas
const GlobalStyles = () => (
  <Global
    styles={css`
      @keyframes fade_eiknz_10 {
        0% { opacity: 0; }
        to { opacity: 1; }
      }
      ._fadeIn_eiknz_10 {
        animation: fade_eiknz_10 var(--duration, 0s) ease forwards calc(10ms + var(--delay, 0s));
        opacity: 0;
      }
      @keyframes fade_1frq2_1 {
        0% { opacity: 0; }
        to { opacity: 1; }
      }
      ._markdown_1frq2_10.markdown .katex-display {
        animation: fade_1frq2_1 0.4s ease 50ms forwards;
        opacity: 0;
      }
    `}
  />
);

// Sistema de colores (igual que en AGT Product)
const appleColors = {
  light: {
    label: { primary: '#000000' },
    system: {
      grey: '#8E8E93',
      grey3: '#C7C7CC',
      green: '#31C859',
      white: '#FFFFFF',
      black: '#000000'
    },
    bg: { primaryElevated: '#FFFFFF' },
    fill: { tertiary: '#767680' },
    ui: { accent: '#007BFE', base: '#F2F2F7' }
  },
  dark: {
    label: { primary: '#FFFFFF' },
    system: {
      grey: '#8E8E93',
      grey3: '#464649',
      green: '#2DD257',
      white: '#FFFFFF',
      black: '#000000'
    },
    bg: { primaryElevated: '#000000' },
    fill: { tertiary: '#767680' },
    ui: { accent: '#0385FF', base: '#272729' }
  }
};

const navigationColors = (theme) => {
  const mode = theme.palette.mode;
  const c = mode === 'light' ? appleColors.light : appleColors.dark;
  return {
    background: c.bg.primaryElevated,
    textDefault: c.label.primary,
    textSecondary: c.system.grey,
    cardBackground: c.ui.base,
    borderColor: c.system.grey3,
    accent: c.ui.accent,
    success: c.system.green,
    error: '#FF3B30',
    warning: '#FF9500'
  };
};

// Keyframe para la animación de fade in
const fadeIn = keyframes`
  0% { opacity: 0; }
  100% { opacity: 1; }
`;

// Componente para palabra animada
const AnimatedWord = styled('span')(({ theme, delay, isPlaying }) => ({
  display: 'inline-block',
  marginRight: '0.25em',
  fontSize: 'inherit',
  opacity: isPlaying ? 0 : 1,
  animation: isPlaying ? `${fadeIn} 0.4s ease forwards ${delay}s` : 'none'
}));

// NUEVO COMPONENTE: FormattedAnimatedMessage
const FormattedAnimatedMessage = ({ content, isPlaying }) => {
  const wordCountRef = useRef(0);

  const wrapWordsInAnimation = useCallback((text) =>
    text.split(' ').map((word) => {
      const currentIndex = wordCountRef.current++;
      return (
        <AnimatedWord key={`${word}-${currentIndex}`} delay={0.05 * currentIndex} isPlaying={true}>
          {word}{' '}
        </AnimatedWord>
      );
    }), []);

  const animatedContent = useMemo(() => {
    wordCountRef.current = 0;
    return (
      <Box style={{ '--duration': '0.4s', '--delay': '0s' }}>
        <ReactMarkdown
          components={{
            p: ({ children }) => <p>{React.Children.map(children, (child) => (typeof child === 'string' ? wrapWordsInAnimation(child) : child))}</p>,
            strong: ({ children }) => <strong>{React.Children.map(children, (child) => (typeof child === 'string' ? wrapWordsInAnimation(child) : child))}</strong>,
            em: ({ children }) => <em>{React.Children.map(children, (child) => (typeof child === 'string' ? wrapWordsInAnimation(child) : child))}</em>,
            li: ({ children }) => <li>{React.Children.map(children, (child) => (typeof child === 'string' ? wrapWordsInAnimation(child) : child))}</li>,
            h1: ({ children }) => <h1>{React.Children.map(children, (child) => (typeof child === 'string' ? wrapWordsInAnimation(child) : child))}</h1>,
            h2: ({ children }) => <h2>{React.Children.map(children, (child) => (typeof child === 'string' ? wrapWordsInAnimation(child) : child))}</h2>
          }}
        >
          {content}
        </ReactMarkdown>
      </Box>
    );
  }, [content, wrapWordsInAnimation]);

  return isPlaying ? animatedContent : <ReactMarkdown>{content}</ReactMarkdown>;
};

// Hook para manejar el último mensaje del asistente
const useLatestMessage = () => {
  const [latestMessageId, setLatestMessageId] = useState(null);

  const updateLatestMessage = useCallback((messages) => {
    const lastMessage = messages[messages.length - 1];
    if (lastMessage && lastMessage.type === 'agt') {
      setLatestMessageId(messages.length - 1);
      setTimeout(() => setLatestMessageId(null), 10000);
    }
  }, []);

  return { latestMessageId, updateLatestMessage };
};

// Botón circular para enviar
const CircularSendButton = styled(IconButton)(({ theme }) => {
  const navColors = navigationColors(theme);
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';
  return {
    background: isWallpaperMode ? '#FFFFFF' : navColors.accent,
    color: isWallpaperMode ? '#000000' : navColors.textSecondary,
    borderRadius: '50%',
    width: 28,
    height: 28,
    padding: 4,
    transition: theme.transitions.create('opacity', {
      duration: theme.transitions.duration.short,
      easing: theme.transitions.easing.easeInOut
    }),
    '&:hover': {
      background: isWallpaperMode
        ? 'rgba(255, 255, 255, 0.85)'
        : alpha(navColors.accent, 0.9)
    }
  };
});

// Componente unificado para input/botón
const UnifiedInputButton = styled(Box)(({ theme, isexpanded }) => {
  const navColors = navigationColors(theme);
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';
  return {
    width: '360px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: isWallpaperMode
      ? 'rgba(128, 128, 128, 0.25)'
      : navColors.cardBackground,
    backdropFilter: isWallpaperMode ? 'blur(8px)' : 'none',
    WebkitBackdropFilter: isWallpaperMode ? 'blur(8px)' : 'none',
    borderRadius: '20px',
    padding: '12px',
    transition: theme.transitions.create(['box-shadow', 'background-color'], {
      duration: theme.transitions.duration.complex,
      easing: theme.transitions.easing.easeInOut
    }),
    cursor: 'pointer',
    pointerEvents: 'auto',
    border: isWallpaperMode
      ? '1px solid rgba(128, 128, 128, 0.3)'
      : `1px solid ${navColors.borderColor}`,
    '& *': { cursor: isexpanded === 'true' ? 'text' : 'pointer' }
  };
});

// Input estilizado
const StyledInputBase = styled(InputBase)(({ theme, isexpanded }) => {
  const navColors = navigationColors(theme);
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';
  return {
    width: '100%',
    color: isWallpaperMode ? '#FFFFFF' : navColors.textDefault,
    '& .MuiInputBase-input': {
      padding: '0px',
      cursor: isexpanded === 'true' ? 'text' : 'pointer',
      '&::placeholder': {
        color: isWallpaperMode
          ? 'rgba(255, 255, 255, 0.7)'
          : navColors.textSecondary,
        opacity: 1
      }
    }
  };
});

// Chat container con responsividad
const ChatContainer = styled(Paper)(({ theme }) => {
  const navColors = navigationColors(theme);
  const isSmallScreen = useMediaQuery('(max-height: 700px)');

  return {
    borderRadius: theme.shape.borderRadius * 2,
    background: theme.palette.actualMode === 'wallpaper'
      ? 'rgba(128, 128, 128, 0.3)'
      : navColors.background,
    backdropFilter: theme.palette.actualMode === 'wallpaper'
      ? 'blur(10px)'
      : 'none',
    width: '360px',
    // Ajuste de altura basado en el tamaño de pantalla
    height: isSmallScreen ? '450px' : '600px',
    maxHeight: '80vh', // Garantiza que nunca exceda el 80% de la altura de la ventana
    display: 'flex',
    flexDirection: 'column',
    boxShadow: 'none',
    border: theme.palette.actualMode === 'wallpaper'
      ? '1px solid rgba(128, 128, 128, 0.3)'
      : `1px solid ${navColors.borderColor}`,
    transition: theme.transitions.create(['opacity', 'transform'], {
      duration: theme.transitions.duration.complex,
      easing: theme.transitions.easing.easeInOut
    })
  };
});

// Encabezado del chat
const ChatHeader = styled(Box)(({ theme }) => {
  const navColors = navigationColors(theme);
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';
  return {
    padding: theme.spacing(2),
    background: isWallpaperMode ? 'transparent' : navColors.background,
    borderTopLeftRadius: theme.shape.borderRadius * 2,
    borderTopRightRadius: theme.shape.borderRadius * 2,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative'
  };
});

// Área de mensajes con scroll manual (quitamos scrollBehavior: 'smooth')
const MessageArea = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  flexGrow: 1,
  overflowY: 'auto',
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
  '&::-webkit-scrollbar': { width: 6 },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: alpha(theme.palette.mode === 'light' ? '#000' : '#FFF', 0.4),
    borderRadius: 3
  },
  '&::-webkit-scrollbar-track': { background: 'transparent' }
}));

// Componente Message con estilos adaptativos
const Message = styled(Box)(({ theme, variant, content }) => {
  const navColors = navigationColors(theme);
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';
  const getMessageRadius = (messageContent) => {
    const contentLength = messageContent?.length || 0;
    if (contentLength < 20) return '9999px';
    else if (contentLength < 50) return '24px';
    else return '16px';
  };

  const baseStyles = {
    maxWidth: '80%',
    padding: theme.spacing(1.5),
    borderRadius: getMessageRadius(content),
    color: isWallpaperMode ? '#FFFFFF' : navColors.textDefault,
    textTransform: 'none'
  };

  if (variant === 'user') {
    return {
      ...baseStyles,
      alignSelf: 'flex-end',
      background: isWallpaperMode
        ? 'rgba(128, 128, 128, 0.25)'
        : navColors.cardBackground,
      backdropFilter: isWallpaperMode ? 'blur(8px)' : 'none',
      WebkitBackdropFilter: isWallpaperMode ? 'blur(8px)' : 'none',
      border: isWallpaperMode ? '1px solid rgba(128, 128, 128, 0.3)' : 'none'
    };
  }
  return {
    ...baseStyles,
    alignSelf: 'flex-start',
    background: 'transparent',
    color: isWallpaperMode ? '#FFFFFF' : (theme.palette.mode === 'light' ? '#000000' : '#FFFFFF')
  };
});

// Contenedores de prompts (mobile y desktop)
const PromptContainerMobile = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  marginBottom: theme.spacing(1),
  overflowX: 'auto',
  whiteSpace: 'nowrap',
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(2),
  gap: theme.spacing(2),
  '&::-webkit-scrollbar': { display: 'none' }
}));

const PromptContainerDesktop = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(1),
  display: 'flex',
  justifyContent: 'center',
  gap: theme.spacing(1),
  flexWrap: 'wrap'
}));

const PromptCard = styled(Box)(({ theme }) => {
  const navColors = navigationColors(theme);
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';
  return {
    background: isWallpaperMode
      ? 'rgba(128, 128, 128, 0.25)'
      : navColors.cardBackground,
    backdropFilter: isWallpaperMode ? 'blur(8px)' : 'none',
    WebkitBackdropFilter: isWallpaperMode ? 'blur(8px)' : 'none',
    border: isWallpaperMode ? '1px solid rgba(128, 128, 128, 0.3)' : 'none',
    color: isWallpaperMode ? '#FFFFFF' : navColors.textDefault,
    padding: theme.spacing(1),
    borderRadius: '9999px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    flexShrink: 0
  };
});

const FooterText = styled(Typography)(({ theme }) => ({
  textAlign: 'center',
  marginTop: theme.spacing(1),
  fontSize: '0.65rem',
  color: theme.palette.actualMode === 'wallpaper'
    ? 'rgba(255, 255, 255, 0.7)'
    : '#808080'
}));

const MarkdownContent = styled(Box)(({ theme }) => {
  const navColors = navigationColors(theme);
  return {
    fontSize: '0.875rem',
    lineHeight: 1.43,
    '& > *:first-of-type': { marginTop: 0 },
    '& > *:last-child': { marginBottom: 0 },
    '& p': {
      margin: 0,
      color: navColors.textDefault,
      fontSize: '0.875rem',
      lineHeight: 1.43
    },
    '& ul, & ol': {
      marginTop: theme.spacing(1),
      marginBottom: theme.spacing(1),
      paddingLeft: theme.spacing(2),
      '& li': {
        fontSize: '0.875rem',
        marginBottom: '0.25em',
        '&::marker': { fontSize: '0.875rem', color: navColors.textDefault }
      }
    },
    '& code': {
      backgroundColor: alpha(navColors.textDefault, 0.1),
      padding: '2px 4px',
      borderRadius: 4,
      fontSize: '0.875rem'
    },
    '& pre': {
      backgroundColor: theme.palette.mode === 'dark'
        ? alpha(navColors.textDefault, 0.15)
        : alpha(navColors.textDefault, 0.1),
      padding: theme.spacing(1),
      borderRadius: 4,
      overflow: 'auto',
      '& code': {
        backgroundColor: 'transparent',
        padding: 0,
        fontSize: '0.875rem',
        color: theme.palette.mode === 'dark' ? '#e6e6e6' : 'inherit'
      }
    },
    '& blockquote': {
      borderLeft: `4px solid ${alpha(navColors.textDefault, 0.2)}`,
      margin: theme.spacing(1, 0),
      padding: theme.spacing(0, 1),
      fontSize: '0.875rem'
    },
    '& a': {
      color: navColors.accent,
      textDecoration: 'none',
      fontSize: '0.875rem',
      '&:hover': { textDecoration: 'underline' }
    }
  };
});

// Mapeo para convertir el índice de pestaña en nombre de sección
const tabMapping = {
  0: 'Balance',
  1: 'Transactions',
  2: 'Payments',
  3: 'Budget',
  4: 'Upload'
};

const renderMessageContent = (message, navColors, assistantTextColor, isNewMessage) => {
  const tagMapping = {
    '<agt_success_create>': { bg: navColors.success, text: 'Operation successful', icon: <CheckCircle size={16} color="white" /> },
    '<agt_fail_create>': { bg: navColors.error, text: 'Operation failed', icon: <Frown size={16} color="white" /> }
  };

  if (message.type === 'agt' && tagMapping[message.content]) {
    const { bg, text, icon } = tagMapping[message.content];
    return (
      <Box
        sx={{
          background: bg,
          borderRadius: '9999px',
          padding: 1,
          display: 'flex',
          alignItems: 'center',
          animation: isNewMessage ? `${fadeIn} 0.4s ease forwards 0.05s` : 'none',
          opacity: isNewMessage ? 0 : 1
        }}
      >
        {icon}
        <Typography variant="body2" sx={{ ml: 1, color: '#FFFFFF', textTransform: 'none' }}>
          {text}
        </Typography>
      </Box>
    );
  }

  if (message.type === 'agt') {
    return (
      <MarkdownContent className="markdown _markdown_1frq2_10">
        <FormattedAnimatedMessage content={message.content} isPlaying={isNewMessage} />
      </MarkdownContent>
    );
  }

  return (
    <Typography variant="body2" sx={{ color: assistantTextColor, textTransform: 'none' }}>
      {message.content}
    </Typography>
  );
};

const AGTFloatingChatCashFlow = ({ activeTab }) => {
  const theme = useTheme();
  const navColors = navigationColors(theme);
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [isExpanded, setIsExpanded] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const messageAreaRef = useRef(null);
  const inputRef = useRef(null);
  const shouldFocusRef = useRef(false);
  const { latestMessageId, updateLatestMessage } = useLatestMessage();
  const assistantTextColor = theme.palette.mode === 'light' ? '#000000' : '#FFFFFF';
  const arrowColor = inputValue.trim() ? 'white' : navColors.textSecondary;
  // Bandera para controlar si se debe hacer scroll al añadir un mensaje
  const [shouldScrollToBottom, setShouldScrollToBottom] = useState(false);

  // Función para hacer scroll hasta el final
  const scrollToBottom = useCallback(() => {
    if (messageAreaRef.current) {
      messageAreaRef.current.scrollTop = messageAreaRef.current.scrollHeight;
    }
  }, []);

  // Efecto para hacer autofocus en el input cuando se abre el chat
  useEffect(() => {
    if (isExpanded && inputRef.current && shouldFocusRef.current) {
      // Añadimos setTimeout para asegurar que el focus ocurre después de que el componente está completamente renderizado
      setTimeout(() => {
        inputRef.current.focus();
        shouldFocusRef.current = false;
      }, 50);
    }
  }, [isExpanded]);

  // Efecto para hacer scroll al fondo cuando se abre el chat con mensajes existentes
  useEffect(() => {
    if (isExpanded && messageAreaRef.current) {
      scrollToBottom();
    }
  }, [isExpanded, scrollToBottom]);

  // Efecto para realizar scroll solo cuando es necesario
  useEffect(() => {
    if (shouldScrollToBottom) {
      scrollToBottom();
      setShouldScrollToBottom(false);
    }
  }, [shouldScrollToBottom, scrollToBottom]);

  // Efecto para actualizar el último mensaje
  useEffect(() => {
    if (messages.length > 0) {
      updateLatestMessage(messages);
    }
  }, [messages, updateLatestMessage]);

  const promptOptions = [
    { label: 'Generate Balance Overview', icon: <PlusCircle size={16} /> },
    { label: 'View Recent Transactions', icon: <ClipboardList size={16} /> },
    { label: 'Check Upcoming Payments', icon: <Trash2 size={16} /> }
  ];

  // Obtén el nombre de la sección según el mapeo
  const currentSection = tabMapping[activeTab] || 'Unknown';

  const handlePromptClick = async (promptText) => {
    if (isLoading) return;
    try {
      setIsLoading(true);
      setMessages((prev) => [...prev, { type: 'user', content: promptText }]);
      // Indicar que se debe hacer scroll al fondo después de añadir el mensaje del usuario
      setShouldScrollToBottom(true);
      
      const response = await agtCashFlowService.sendMessage(promptText, {
        activeTab: currentSection,
        timestamp: new Date().toISOString()
      });
      setMessages((prev) => [
        ...prev,
        { type: 'agt', content: response.data.data.message }
      ]);
      // NO hacemos scroll después de añadir la respuesta del asistente
    } catch (error) {
      setMessages((prev) => [
        ...prev,
        { type: 'agt', content: 'I apologize, but I encountered an error. Could you please try again?' }
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendMessage = async () => {
    if (!inputValue.trim() || isLoading) return;
    try {
      setIsLoading(true);
      setMessages((prev) => [...prev, { type: 'user', content: inputValue }]);
      // Indicar que se debe hacer scroll al fondo después de añadir el mensaje del usuario
      setShouldScrollToBottom(true);
      setInputValue('');
      
      const response = await agtCashFlowService.sendMessage(inputValue, {
        activeTab: currentSection,
        timestamp: new Date().toISOString()
      });
      setMessages((prev) => [
        ...prev,
        { type: 'agt', content: response.data.data.message }
      ]);
      // NO hacemos scroll después de añadir la respuesta del asistente
    } catch (error) {
      setMessages((prev) => [
        ...prev,
        { type: 'agt', content: 'I apologize, but I encountered an error. Could you please try again?' }
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <>
      <GlobalStyles />
      <Box
        sx={{
          position: 'fixed',
          bottom: 20,
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: theme.zIndex.drawer + 1
        }}
      >
        <ClickAwayListener onClickAway={() => setIsExpanded(false)}>
          <Box>
            {isExpanded && (
              <Box sx={{ marginBottom: 2 }}>
                <ChatContainer onMouseDown={(e) => e.stopPropagation()}>
                  <ChatHeader>
                    <Typography variant="subtitle1" sx={{ textTransform: 'none', color: navColors.textDefault, fontWeight: 500 }}>
                      AGT Cash Flow Assistant
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        setIsExpanded(false);
                      }}
                      sx={{ color: navColors.textDefault, position: 'absolute', right: theme.spacing(2) }}
                    >
                      <Minimize2 size={18} />
                    </IconButton>
                  </ChatHeader>
                  <MessageArea ref={messageAreaRef} onMouseDown={(e) => e.stopPropagation()}>
                    {messages.map((message, index) => (
                      <Message key={index} variant={message.type} content={message.content}>
                        {renderMessageContent(message, navColors, assistantTextColor, index === latestMessageId)}
                      </Message>
                    ))}
                    {isLoading && (
                      <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                        <CircularProgress size={24} />
                      </Box>
                    )}
                    <div ref={messagesEndRef} />
                  </MessageArea>
                  {messages.length === 0 &&
                    (isMobile ? (
                      <PromptContainerMobile onMouseDown={(e) => e.stopPropagation()}>
                        {promptOptions.map((option, idx) => (
                          <PromptCard
                            key={idx}
                            onMouseDown={(e) => {
                              e.stopPropagation();
                              handlePromptClick(option.label);
                            }}
                          >
                            {option.icon}
                            <Typography variant="body2" sx={{ textTransform: 'none' }}>
                              {option.label}
                            </Typography>
                          </PromptCard>
                        ))}
                      </PromptContainerMobile>
                    ) : (
                      <PromptContainerDesktop onMouseDown={(e) => e.stopPropagation()}>
                        {promptOptions.map((option, idx) => (
                          <PromptCard
                            key={idx}
                            onMouseDown={(e) => {
                              e.stopPropagation();
                              handlePromptClick(option.label);
                            }}
                          >
                            {option.icon}
                            <Typography variant="body2" sx={{ textTransform: 'none' }}>
                              {option.label}
                            </Typography>
                          </PromptCard>
                        ))}
                      </PromptContainerDesktop>
                    ))}
                  <FooterText>Double-check AGT actions.</FooterText>
                </ChatContainer>
              </Box>
            )}
            <UnifiedInputButton
              isexpanded={isExpanded.toString()}
              onMouseDown={(e) => {
                e.stopPropagation();
                if (!isExpanded) {
                  shouldFocusRef.current = true;
                  setIsExpanded(true);
                }
              }}
            >
              <StyledInputBase
                inputRef={inputRef}
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="How can AGT help you?"
                disabled={!isExpanded}
                multiline
                maxRows={4}
                isexpanded={isExpanded.toString()}
                sx={{ pointerEvents: isExpanded ? 'auto' : 'none' }}
              />
              {isExpanded ? (
                <CircularSendButton
                  onMouseDown={(e) => {
                    e.stopPropagation();
                    handleSendMessage();
                  }}
                  disabled={!inputValue.trim() || isLoading}
                >
                  <ArrowUp size={20} color={arrowColor} />
                </CircularSendButton>
              ) : (
                <ArrowUp size={20} color={arrowColor} />
              )}
            </UnifiedInputButton>
          </Box>
        </ClickAwayListener>
      </Box>
    </>
  );
};

export default AGTFloatingChatCashFlow;